import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Zap, Sparkles, Clock } from "lucide-react";

/**
 * SidebarStatusBadge — the "who you are right now" pill for the
 * desktop sidebar. Renders one of 6 variants based on user state:
 *
 *   1. Admin              → gold Admin badge
 *   2. Pro (expiring)     → red renew-Pro CTA
 *   3. Pro (active)       → green Pro Active badge
 *   4. Trial (locked)     → red write-locked badge
 *   5. Trial (waitlist)   → warm waitlist badge (INR/PKR post-trial)
 *   6. Trial (countdown)  → green N-days-left badge
 *   + Trial upgrade CTA   → always shown alongside variants 4-6
 *
 * Kept as one component (not 6 sub-components) because the variants
 * share layout primitives + branching drives entirely off derived
 * state. Extraction gives DesktopSidebar a clear render pipeline
 * without prop-drilling more than we already do.
 */
export default function SidebarStatusBadge({
  isAdmin,
  isPro,
  billingStatus,
  trialStatus,
  daysUntilExpiry,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Variant 1 — admin overrides everything.
  if (isAdmin) {
    return (
      <div
        className="mt-3 mx-1 px-3 py-2.5 rounded-lg border flex items-center gap-2"
        style={{
          background: "linear-gradient(135deg, rgba(224,158,80,0.14) 0%, rgba(45,90,64,0.10) 100%)",
          borderColor: "rgba(224,158,80,0.42)",
        }}
        data-testid="sidebar-admin-badge"
      >
        <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: "#E09E50" }}>
          <ShieldCheck size={13} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold tracking-wider uppercase" style={{ color: "#E09E50" }}>
            {t("sidebar.admin_active", { defaultValue: "Admin" })}
          </div>
          <div className="text-[10px] text-[var(--text-muted)] truncate">
            {t("sidebar.admin_hint", { defaultValue: "Full access — no limits" })}
          </div>
        </div>
      </div>
    );
  }

  // No billing data yet — render nothing (prevents flash of upgrade CTA).
  if (!billingStatus) return null;

  // Variants 2-3 — Pro user.
  if (isPro) {
    if (daysUntilExpiry !== null) {
      return (
        <button
          onClick={() => navigate("/pricing")}
          data-testid="sidebar-pro-expiring-cta"
          className="mt-3 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5 rounded-lg border flex items-center gap-2 transition-all hover:shadow-sm text-left animate-fadeup"
          style={{
            background: "linear-gradient(135deg, rgba(196,91,77,0.10) 0%, rgba(224,158,80,0.12) 100%)",
            borderColor: "rgba(196,91,77,0.40)",
          }}
          title="Renew before your Pro period ends"
        >
          <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: "#C45B4D" }}>
            <Zap size={13} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium" style={{ color: "var(--text-primary)" }}>
              {t("sidebar.renew_pro")}
            </div>
            <div className="text-[10px] text-[var(--text-muted)] truncate">
              {t("sidebar.expires_in_days", { count: daysUntilExpiry })}
            </div>
          </div>
        </button>
      );
    }
    return (
      <div
        className="mt-3 mx-1 px-3 py-2.5 rounded-lg border flex items-center gap-2"
        style={{
          background: "linear-gradient(135deg, rgba(45,90,64,0.08) 0%, rgba(224,158,80,0.08) 100%)",
          borderColor: "rgba(45,90,64,0.28)",
        }}
        data-testid="sidebar-pro-badge"
      >
        <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: "var(--accent-primary)" }}>
          <Sparkles size={13} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold tracking-wider uppercase" style={{ color: "var(--accent-primary)" }}>
            {t("sidebar.pro_active")}
          </div>
          <div className="text-[10px] text-[var(--text-muted)] truncate">
            {billingStatus.cancel_at_period_end ? t("sidebar.pro_ending") : t("sidebar.pro_active_hint")}
          </div>
        </div>
      </div>
    );
  }

  // Variants 4-6 — free-tier user in a trial state (+ upgrade CTA).
  const showTrial = trialStatus && trialStatus.trial_ends_at && !trialStatus.is_pro;

  return (
    <>
      {showTrial && (
        trialStatus.is_locked ? (
          <div
            className="mt-3 mx-1 px-3 py-2.5 rounded-lg border flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, rgba(196,91,77,0.10) 0%, rgba(224,158,80,0.10) 100%)",
              borderColor: "rgba(196,91,77,0.35)",
            }}
            data-testid="sidebar-trial-locked"
          >
            <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: "#C45B4D" }}>
              <Clock size={13} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold tracking-wider uppercase" style={{ color: "#C45B4D" }}>
                {t("sidebar.trial_locked_title")}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] truncate">
                {t("sidebar.trial_locked_hint")}
              </div>
            </div>
          </div>
        ) : trialStatus.is_expired && trialStatus.is_waitlist_region ? (
          <div
            className="mt-3 mx-1 px-3 py-2.5 rounded-lg border flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, rgba(224,158,80,0.08) 0%, rgba(45,90,64,0.05) 100%)",
              borderColor: "rgba(224,158,80,0.35)",
            }}
            data-testid="sidebar-trial-waitlist"
          >
            <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: "var(--accent-warm)" }}>
              <Clock size={13} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold tracking-wider uppercase" style={{ color: "var(--accent-warm)" }}>
                {t("sidebar.trial_waitlist_title")}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] truncate">
                {t("sidebar.trial_waitlist_hint")}
              </div>
            </div>
          </div>
        ) : (typeof trialStatus.days_remaining === "number" && !trialStatus.is_expired && (
          <div
            className="mt-3 mx-1 px-3 py-2.5 rounded-lg border flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.06) 100%)",
              borderColor: "rgba(45,90,64,0.25)",
            }}
            data-testid="sidebar-trial-countdown"
          >
            <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: "var(--accent-primary)" }}>
              <Clock size={13} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold tracking-wider uppercase" style={{ color: "var(--accent-primary)" }}>
                {t("sidebar.trial_countdown_eyebrow")}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] truncate" data-testid="sidebar-trial-days">
                {t("sidebar.trial_days_left", { count: trialStatus.days_remaining })}
              </div>
            </div>
          </div>
        ))
      )}
      <button
        onClick={() => navigate("/pricing")}
        data-testid="sidebar-upgrade-cta"
        className="mt-3 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5 rounded-lg border flex items-center gap-2 transition-all hover:shadow-sm text-left"
        style={{
          background: "linear-gradient(135deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.08) 100%)",
          borderColor: "rgba(224,158,80,0.35)",
        }}
      >
        <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: "var(--accent-warm)" }}>
          <Zap size={13} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-medium" style={{ color: "var(--text-primary)" }}>
            {t("sidebar.upgrade_pro")}
          </div>
          <div className="text-[10px] text-[var(--text-muted)] truncate">
            {t("sidebar.upgrade_hint")}
          </div>
        </div>
      </button>
    </>
  );
}
